import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateModality } from '@/hooks/use-create-modality'
import type { NewModality } from '@/hooks/use-create-modality'
import { Save, ChevronLeft } from 'lucide-react'
import { handleConfirmNavigation } from '../ui/confirm-navigation-popup'
import { ConfirmModal } from '../ui/confirmation'
// import { handleConfirmNavigation } from '../utils/confirmNavigation'; // adjust path
import { getUserName } from "../../utils/user-utils";
import { PageContainer } from '@/component/layout'

export const AddModality = () => {
  const navigate = useNavigate()
  const { createModality, submitting, error, setError } = useCreateModality()

  useEffect(() => {
    const fetchUserName = async () => {
      const name = await getUserName()
      setForm((prev) => ({ ...prev, createdBy: name }))
    }
    fetchUserName()
  }, [])

  const [form, setForm] = useState<NewModality>({
    name: '',
    description: '',
    keyFeatures: '',
    status: false,
    isManualEntry: false,
    changeDescription: '',
    createdBy: '',
  })

  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const onChange = (key: keyof NewModality) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.currentTarget.type === 'checkbox' ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const validate = () => {
    const errs: string[] = []
    if (!form.name?.trim()) errs.push('Name is required.')
    if (!form.description?.trim()) errs.push('Description is required.')
    return errs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const errs = validate()
    if (errs.length) {
      setTouched({ name: true, description: true })
      setError(errs.join(' '))
      return
    }
    try {
      await createModality(form)
      navigate('/modalities', { state: { created: true } })
    } catch {
      // error state already set by hook
    }
  }

  return (
    <PageContainer className='w-[60vw]'>
      {/* Back link */}
      <div className='mb-6'>
          <button
            onClick={() => handleConfirmNavigation(navigate, '/modalities')}
            className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900'
          >
            <span className='-ml-1'>
              <ChevronLeft />
            </span>
            Back to Modality Manager
          </button>

        {/* <Link to='/modalities' className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900'>
          <span className='-ml-1'>
            <ChevronLeft />
          </span>{' '}
          Back to Modality Manager
        </Link> */}
      </div>

      <form onSubmit={handleSubmit} className='space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between mb-6'>
          <h1 className='text-3xl font-bold'>Add new modality</h1>
          {/* <Link to='/' className='underline'>
          ← Back to list
        </Link> */}
          <button type='submit' disabled={submitting} className='rounded-lg bg-black text-white px-4 py-2 hover:bg-gray-800 disabled:opacity-60'>
            {submitting ? 'Saving…' : 'Save'}
            {/* <Save size={20} /> */}
            <Save className='inline-block ml-2' size={18} />
          </button>
        </div>

        {error ? <div className='mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700'>{error}</div> : null}

        {/* Name */}
        <div>
          <label className='block text-sm-semibold text-gray-700 mb-1 font-semibold'>Add modality name</label>
          <input
            type='text'
            value={form.name}
            onChange={onChange('name')}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            className={`w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 ${
              touched.name && !form.name?.trim() ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-300'
            }`}
            placeholder='Type modality name here..'
          />
          {touched.name && !form.name?.trim() && <p className='mt-1 text-sm text-red-600'>Name is required.</p>}
        </div>

        {/* Description */}
        <div>
          <label className='block text-sm font-semibold text-gray-700 mb-1'> Add modality Description</label>
          <textarea
            value={form.description}
            onChange={onChange('description')}
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
            rows={4}
            className={`w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 ${
              touched.description && !form.description?.trim() ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-300'
            }`}
            placeholder='Start typing here…'
          />
          {touched.description && !form.description?.trim() && <p className='mt-1 text-sm text-red-600'>Description is required.</p>}
        </div>
      </form>
      <ConfirmModal />
    </PageContainer>
  )
}
